import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, Renderer2, signal, Signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { OrganizationalUnitPositionUser } from '@backModels/Auth.OrganizationalUnitPositionUser';
import { User } from '@backModels/Auth.User';
import { FeatureId } from '@backModels/FeatureId';
import { OuTypeId } from '@backModels/Ref.OuTypeId';
import { isDepartmentNode } from '@helpers/position-department-node.helper';
import { resolveCampaignEntryRoute } from '@helpers/campaign-navigation.helper';
import { SupportedCulture } from '@backModels/Ref.SupportedCulture';
import { getFlagFile } from '@helpers/flag.helper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReferenceDataApi } from '@shared/api/reference-data.api';
import { UsersApi } from '@shared/api/users.api';
import { BrandService } from '@shared/branding/brand.service';
import { BrandSelectorComponent } from '@shared/components/brand-selector/brand-selector.component';
import { ConnectionWarningComponent } from '@shared/components/connection-warning/connection-warning.component';
import { AuthService } from '@shared/services/auth.service';
import { CurrentUserService } from '@shared/services/current-user.service';
import { MobileMenuService } from '@shared/services/mobile-menu.service';
import { MessageNotificationDto, MessageNotificationKind, MessagePollingService } from '@shared/services/message-polling.service';
import { TextsService } from '@shared/services/texts.service';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { Menubar, MenubarModule } from 'primeng/menubar';
import { PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { filter, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';

interface PositionGroup {
  positionInvariantName: string;
  ouTypeGroups: { ouTypeInvariantName: string; ous: { name: string; route?: string[] }[] }[];
}

@Component({
  selector: 'app-header',
  imports: [MenubarModule, AvatarModule, TranslateModule, FormsModule, RouterModule, ButtonModule, PopoverModule, CommonModule, SkeletonModule, ConnectionWarningComponent, BrandSelectorComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  currentUserService = inject(CurrentUserService);
  authService = inject(AuthService);
  translate = inject(TranslateService);
  renderer = inject(Renderer2);
  referenceDataApi = inject(ReferenceDataApi);
  mobileMenuService = inject(MobileMenuService);
  usersApi = inject(UsersApi);
  textsService = inject(TextsService);
  private messagePollingService = inject(MessagePollingService);
  readonly brandService = inject(BrandService);
  isMobileMenuOpen = signal<boolean>(false);
  readonly router = inject(Router);

  isLoggedIn: Signal<boolean | undefined> = signal<boolean | undefined>(undefined);

  showCampaignButton = false;
  private destroy$ = new Subject<void>();

  availableLanguages: SupportedCulture[] = [];
  selectedLanguage: SupportedCulture | undefined;
  listenerAttached = false;
  items: MenuItem[] = [];
  loading = signal(true);
  @ViewChild('menubar') menubar!: Menubar;

  toggleMobileMenu(): void {
    this.mobileMenuService.toggleMobileMenu();
  }

  get unreadNotificationsCount(): number {
    return this.messagePollingService.totalUnreadCount();
  }

  get notificationItems(): MessageNotificationDto[] {
    return this.messagePollingService.notifications();
  }

  get isExternal(): boolean {
    return localStorage.getItem('auth_type') === 'jwt';
  }

  get primaryNotificationItems(): MessageNotificationDto[] {
    return this.notificationItems.filter(n => n.kind !== MessageNotificationKind.Observer);
  }

  get observerNotificationItems(): MessageNotificationDto[] {
    return this.notificationItems.filter(n => n.kind === MessageNotificationKind.Observer);
  }

  async onNotificationClick(notification: MessageNotificationDto, panel: any) {
    if (this.isExternal) {
      return;
    }
    panel?.hide?.();
    if (!notification.campaignGuid || !notification.campaignInstanceGuid) {
      return;
    }

    this.messagePollingService.trackMessagesFor(notification.campaignInstanceGuid);
    this.messagePollingService.setCurrentChat(notification.campaignInstanceGuid);

    await this.router.navigate(['/campaigns', notification.campaignGuid, 'checkups', 'detail', notification.campaignInstanceGuid], {
      queryParams: { openMessages: '1' },
    });
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  @HostListener('window:resize')
  onResize() {
    this.updateMenuBarMobileState();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateMenuBarMobileState();
    });
  }

  private updateMenuBarMobileState() {
    if (this.menubar && this.menubar.el) {
      const isMobile = this.isMobileView();
      const menubarEl = this.menubar.el.nativeElement;

      if (isMobile) {
        this.renderer.addClass(menubarEl, 'p-menubar-mobile');
      } else {
        this.renderer.removeClass(menubarEl, 'p-menubar-mobile');
        this.renderer.removeClass(menubarEl, 'p-menubar-mobile-active');
      }
    }
  }

  constructor() {
    this.isLoggedIn = toSignal(this.authService.isLoggedInStream);

    this.textsService.setTranslateCurrentLoader();

    this.currentUserService.currentUserStream.pipe(takeUntilDestroyed()).subscribe(user => {
      this.loading.set(false);
      //this.translate.onLangChange.subscribe(async () => {
      //this.langReady = true;

      if (user && this.isLoggedIn()) {
        this.updateMenuItems();
        void this.referenceDataApi.getCultures().then(langs => {
          this.availableLanguages = langs;

          const locale = this.currentUserService.currentUser?.cultureCode ?? 'en';

          if (!this.translate.currentLang) this.translate.currentLang = locale;

          this.selectedLanguage = langs.find(l => l.languageCode === (this.translate.currentLang || 'en'));
        });
      }
      //});
    });
  }

  ngOnInit() {
    void this.loadLangs();

    this.checkIfCampaignsRoute(this.router.url);

    // Escuchar cambios de ruta
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.checkIfCampaignsRoute(event.url);
      });
  }

  async loadLangs() {
    const langs = await this.referenceDataApi.getCultures();
    this.availableLanguages = langs;
  }

  ngAfterViewChecked() {
    if (/*this.langReady &&*/ this.menubar && !this.listenerAttached) {
      this.renderer.listen(this.menubar.el.nativeElement, 'mouseleave', () => {
        this.menubar.hide();
      });
      this.listenerAttached = true;
    }
  }

  get environment() {
    return environment.currentEnv;
  }

  get currentUser(): User | undefined {
    return this.currentUserService.currentUser;
  }

  get currentUserPositions(): OrganizationalUnitPositionUser[] | undefined {
    return this.currentUserService.currentUserPositions;
  }

  getInitials(currentUser: string | undefined): string {
    if (!currentUser) return ''; // Si no hay nombre, devuelve vacío
    const names = currentUser.split(' ');
    return names.length > 1
      ? names[0][0] + names[names.length - 1][0] // Primera y última inicial
      : names[0][0]; // Solo la primera inicial si hay un solo nombre
  }

  async onLanguageSelect(language: SupportedCulture, popover: any) {
    setTimeout(() => {
      this.changeLanguage(language); // Cambia el idioma
      window.location.reload();
    }, 260);
    popover.hide(); // Cierra el popover
    // Actualizar la cultura del usuario de manera eficiente
    await this.updateUserCulture(language);
    this.referenceDataApi.clearCache();
  }

  updateUserCulture(language: SupportedCulture) {
    const user = this.currentUserService.currentUser;
    // Si tienes el 'cultureCode' directamente desde el backend, lo pasas aquí
    const cultureCode = language.cultureCode;
    return this.usersApi.updateUserCulture(user?.userGuid, cultureCode);
  }

  changeLanguage(language: SupportedCulture) {
    this.selectedLanguage = language;
    this.translate.use(language.languageCode!);
  }

  languageCodeFromCulture(): string {
    const cultureCode = this.currentUserService.currentUser?.cultureCode;
    if (cultureCode) {
      const culture = this.availableLanguages.find(x => x.cultureCode === cultureCode);
      return culture?.languageCode?.toUpperCase() ?? 'EN';
    }
    return 'EN'; // Valor predeterminado
  }

  getFlagFile(language: SupportedCulture): string {
    return getFlagFile(language.languageCode);
  }

  isMobileView(): boolean {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 1024;
    }
    return false;
  }

  private updateMenuItems() {
    this.items = [
      {
        label: 'Campaigns',
        icon: 'analytics',
        styleClass: '',
        routerLink: ['/campaigns'],
        visible: this.currentUserService.isReadAllowed([FeatureId.Campaigns]),
      },
      {
        label: 'Companies',
        icon: 'domain',
        styleClass: '',
        routerLink: ['/companies'],
        visible: this.currentUserService.isReadAllowed([FeatureId.Companies]),
      },
      {
        label: 'Admin',
        icon: 'manufacturing',
        styleClass: 'test',
        routerLink: ['/admin'],
        visible: this.currentUserService.isReadAllowed([FeatureId.Admin]),
      },
      // {
      //   label: 'ValidationComponents',
      //   icon: 'check_circle',
      //   styleClass: 'test',
      //   routerLink: ['/validation-components'],
      //   visible: !environment.production,
      // },
      // {
      //   label: 'FormVisualGuide',
      //   icon: 'stylus_note',
      //   styleClass: 'test',
      //   routerLink: ['/form-visual-guide'],
      //   visible: !environment.production,
      // },
    ];
  }

  /** Called from the template on every change detection: rebuilt only when the positions change. */
  private positionGroupsCache: { positions: OrganizationalUnitPositionUser[]; groups: PositionGroup[] } | undefined;

  /** Campaigns link to the same entry page as the campaign list; other OU types are plain text. */
  groupByPositionInvariantName(): PositionGroup[] {
    const positions = this.currentUserPositions;
    if (!positions) return [];
    if (this.positionGroupsCache?.positions === positions) return this.positionGroupsCache.groups;

    // Group by position, then by OU type; each OU name keeps its route (campaigns only)
    const positionMap = new Map<string, Map<string, Map<string, string[] | undefined>>>();

    positions
      .filter(pos => pos.ouTypeId !== OuTypeId.System && !isDepartmentNode(pos))
      .forEach(pos => {
        const posName = pos.positionInvariantName!;
        const ouTypeName = pos.ouTypeInvariantName || 'Other';
        const ouName = pos.ouName!;

        if (!positionMap.has(posName)) {
          positionMap.set(posName, new Map<string, Map<string, string[] | undefined>>());
        }
        const typeMap = positionMap.get(posName)!;
        if (!typeMap.has(ouTypeName)) {
          typeMap.set(ouTypeName, new Map<string, string[] | undefined>());
        }
        const route = pos.ouTypeId === OuTypeId.Campaign && pos.ouGuid ? resolveCampaignEntryRoute(this.currentUserService, pos.ouGuid) : undefined;
        typeMap.get(ouTypeName)!.set(ouName, route);
      });

    const groups = Array.from(positionMap.entries())
      .map(([positionInvariantName, typeMap]) => ({
        positionInvariantName,
        ouTypeGroups: Array.from(typeMap.entries())
          .map(([ouTypeInvariantName, ous]) => ({
            ouTypeInvariantName,
            ous: Array.from(ous.entries())
              .map(([name, route]) => ({ name, route }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          }))
          .sort((a, b) => a.ouTypeInvariantName.localeCompare(b.ouTypeInvariantName)),
      }))
      .sort((a, b) => a.positionInvariantName.localeCompare(b.positionInvariantName));
    this.positionGroupsCache = { positions, groups };
    return groups;
  }

  private checkIfCampaignsRoute(url: string): void {
    const campaignPattern = /\/campaigns\/[a-f0-9-]+/i;
    this.showCampaignButton = campaignPattern.test(url);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
